import { useContext } from "react";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

import { DataContext } from "contexts/Data";
import { StreamContext } from "contexts/Stream";
import useSelectedColor from "hooks/useSelectedColor";
import useGtagVariantData from "hooks/useGtagVariantData";
import { getWasmMethods } from "@3ds/3DEXCITEshow-streaming";

import type { ColorOption } from "types/app";

enum ICON_SIZE {
  WIDTH = 80,
  HEIGHT = 60,
}

export default function ColorSelector() {
  const wasmMethods = getWasmMethods();

  const { availableColors, setCachedState } = useContext(DataContext);
  const { turntableState } = useContext(StreamContext);
  const selectedColor = useSelectedColor();
  const getGtagData = useGtagVariantData();

  const { isTurntable } = turntableState;

  const handleChange = async (newColor: ColorOption) => {
    if (newColor.id === selectedColor?.id) return;

    window.gtag("event", "color_change", getGtagData("model", "Change color to " + newColor.label));

    setCachedState((prev) => ({ ...prev, colorId: newColor.id }));

    try {
      if (!isTurntable && wasmMethods && "switchVariants" in wasmMethods) {
        await wasmMethods?.switchVariants(newColor?.friendly_name);
      } else {
        //
      }
    } catch (ex) {
      console.log(ex, " switch variant cordis error");
    }
  };

  if (!selectedColor) return null;

  return (
    <ColorSelectorContainer>
      <ColorIconContainer>
        {availableColors.map((color) => (
          <Stack
            key={color.id}
            direction="column"
            width={`${ICON_SIZE.WIDTH + 2}px`}
            minHeight={`${ICON_SIZE.HEIGHT + 2}px`}
            rowGap={1}
            sx={{
              overflow: "visible",
            }}
          >
            <BorderDiv selected={selectedColor.id === color.id}>
              <Tooltip title={color.label}>
                <ColorIcon
                  selected={selectedColor.id === color.id}
                  backgroundImg={color.backgroundImg}
                  backgroundColor={color.backgroundColor}
                  onClick={() => handleChange(color)}
                />
              </Tooltip>
            </BorderDiv>

            <Typography
              variant="caption"
              width="100%"
              textAlign="center"
              textTransform="uppercase"
              fontSize="11px"
            >
              {color.label}
            </Typography>
          </Stack>
        ))}
      </ColorIconContainer>
    </ColorSelectorContainer>
  );
}

const ColorSelectorContainer = styled("div")`
  padding-top: 0.5rem;
`;

const ColorIconContainer = styled("div")`
  display: flex;
  column-gap: 1.5rem;
  row-gap: 1.75rem;
  align-items: flex-start;
  scroll-behavior: smooth;
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: #a9a9a9;
    border-radius: 100px;
  }

  &::-webkit-scrollbar-track {
    background-color: #c0c0c0;
    border-radius: 100px;
  }
`;

type BorderDivProps = {
  selected?: boolean;
};

const BorderDiv = styled("div")<BorderDivProps>`
  width: ${ICON_SIZE.WIDTH}px;
  height: ${ICON_SIZE.HEIGHT}px;
  padding: 2px;
  border-radius: 4px;
  border-image: linear-gradient(
      to right,
      rgba(214, 61, 48, 0.75),
      transparent,
      rgba(214, 61, 48, 0.75)
    )
    1;
  border-width: ${({ selected }) => (selected ? 2.5 : 0)}px;
  border-style: solid;
  transition: all 0.2s ease;
`;

type ColorIconProps = {
  selected?: boolean;
  backgroundImg?: string;
  backgroundColor: string;
};

const ColorIcon = styled("div")<ColorIconProps>`
  width: 100%;
  height: 100%;
  border-radius: 4px;
  background-color: ${({ backgroundColor }) => backgroundColor};
  background-image: ${({ backgroundImg }) => backgroundImg};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  cursor: pointer;
  transition: all 0.2s ease;
`;
